import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import {
  pedagog,
  student,
} from '../../../components/common/common-functions/common-functions';
import {
  getAcademicSubjectPerPedagogUrl,
  getAcademikSubjectUrl,
} from '../../../components/common/common-functions/commonApiUrl/get-academik-subject-url';
import { getTopicUrl } from '../../../components/common/common-functions/commonApiUrl/get-topic-url';
import { handleDownloadResponse } from '../../../components/common/common-functions/handle-response';
import getConfig from '../../../helpers/config';
import { IdEntity } from '../../../interfaces/common-interfaces';
import { UpdateKlasaStudent } from '../../../interfaces/klasa-interface';
import {
  CreateLendaAkademikeProps,
  KlasaLendaResponse,
  LendaAkademikeByIdProps,
  LendaAkademikeProps,
  LendaTemaParams,
  LendaTemaResponse,
  LendaTemat,
  LendaTematProps,
  MaterialsPerTopic,
  PedagoguDetailsProps,
  UpdateDetyraPedagog,
  UpdateLendaBody,
  UpdateMaterialPedagog,
  UpdatePedagogPerSubject,
  UploadDetyraParams,
} from '../../../interfaces/lenda-akademike.interface';
import {
  PaginationProps,
  PaginationReturn,
} from '../../../interfaces/pagination';
import { getPrepareHeaders } from '../get-prepare-headers';
import { LendaApiProps } from './lendaApi';

const { test } = getConfig();

type Materials = PaginationReturn<MaterialsPerTopic>;
type FormDataType = UpdateKlasaStudent<FormData>;
type AcademicSubject = PaginationReturn<LendaAkademikeProps>;

interface MaterialsProps extends PaginationProps {
  id: number;
}

const getUrl = ({ id, role }: IdEntity) => {
  return role === pedagog
    ? `/materiale/${id}/dokument/per-pedagog`
    : role === student
      ? `/materiale/${id}/dokument/per-student`
      : `/materiale/${id}/dokument`;
};

const getDetyraUrl = ({ id, role }: IdEntity) => {
  return role === pedagog
    ? `/detyra/${id}/dokument-per-pedagog`
    : `/detyra/${id}/dokument-per-student`;
};

export const lendeVitiAkademikApi = createApi({
  reducerPath: 'lendeVitiAkademik',
  baseQuery: fetchBaseQuery({
    baseUrl: test.baseUrl + `/`,
    prepareHeaders: getPrepareHeaders,
  }),
  tagTypes: ['allAcademicSubjects'],
  endpoints: builder => ({
    getAllAcademicSubjects: builder.query<AcademicSubject, LendaApiProps>({
      query: args => ({
        url: getAcademikSubjectUrl(args),
      }),
      providesTags: ['allAcademicSubjects'],
    }),
    getAcademicSubjectById: builder.query<LendaAkademikeByIdProps, number>({
      query: id => ({
        url: `/lenda-akademike/${id}`,
      }),
      providesTags: ['allAcademicSubjects'],
    }),
    getKlasaLenda: builder.query<KlasaLendaResponse, number>({
      query: id => ({
        url: `/lenda-akademike/${id}/klasaLenda`,
      }),
      providesTags: ['allAcademicSubjects'],
    }),
    getLendaAkademikePerPedagog: builder.query<KlasaLendaResponse, number>({
      query: id => ({
        url: `/lenda-akademike/${id}/per-pedagog`,
      }),
      providesTags: ['allAcademicSubjects'],
    }),
    getKlasaLendaPerPedagog: builder.query<KlasaLendaResponse, number>({
      query: id => ({
        url: `/lenda-akademike/${id}/klasa-per-pedagog`,
      }),
      providesTags: ['allAcademicSubjects'],
    }),
    getTemaPerLende: builder.query<LendaTemaResponse, LendaTemaParams>({
      query: args => ({
        url: getTopicUrl(args, ''),
      }),
      providesTags: ['allAcademicSubjects'],
    }),
    getTemaPerLendeStudent: builder.query<LendaTemaResponse, LendaTemaParams>({
      query: args => ({
        url: getTopicUrl(args, '/per-student'),
      }),
      providesTags: ['allAcademicSubjects'],
    }),
    getTemaPerLendeAdmin: builder.query<LendaTemaResponse, LendaTemaParams>({
      query: args => ({
        url: getTopicUrl(args, '/per-lende-akademike'),
      }),
      providesTags: ['allAcademicSubjects'],
    }),
    addAcademicSubject: builder.mutation<number, CreateLendaAkademikeProps>({
      query: body => ({
        url: '/lenda-akademike',
        method: 'POST',
        body: body,
      }),
      invalidatesTags: ['allAcademicSubjects'],
    }),
    updateAcademicSubject: builder.mutation<
      number,
      UpdateKlasaStudent<UpdateLendaBody>
    >({
      query: ({ body, id }) => ({
        url: `/lenda-akademike/${id}`,
        method: 'PUT',
        body: body,
      }),
      invalidatesTags: ['allAcademicSubjects'],
    }),
    updateTopicsPerSubject: builder.mutation<
      number,
      UpdateKlasaStudent<LendaTematProps[]>
    >({
      query: ({ body, id }) => ({
        url: `/lenda-akademike/${id}/lenda-tema`,
        method: 'PUT',
        body: body,
      }),
      invalidatesTags: ['allAcademicSubjects'],
    }),
    updateSingleTopic: builder.mutation<number, UpdateKlasaStudent<LendaTemat>>(
      {
        query: ({ body, id }) => ({
          url: `/lenda-tema/${id}`,
          method: 'PUT',
          body: body,
        }),
        invalidatesTags: ['allAcademicSubjects'],
      },
    ),
    updateSingleTeacher: builder.mutation<
      number,
      UpdateKlasaStudent<PedagoguDetailsProps>
    >({
      query: ({ body, id }) => ({
        url: `/lenda-pedagog/${id}`,
        method: 'PUT',
        body: body,
      }),
      invalidatesTags: ['allAcademicSubjects'],
    }),
    updateTeachersPerSuubject: builder.mutation<
      number,
      UpdateKlasaStudent<UpdatePedagogPerSubject[]>
    >({
      query: ({ body, id }) => ({
        url: `lenda-akademike/${id}/pedagog`,
        method: 'PUT',
        body: body,
      }),
      invalidatesTags: ['allAcademicSubjects'],
    }),
    deleteAcademicSubject: builder.mutation<string, number>({
      query: id => ({
        url: `/lenda-akademike/${id}`,
        method: 'DELETE',
        responseHandler: response => response.text(),
      }),
      invalidatesTags: ['allAcademicSubjects'],
    }),
    getAcademicSubjectForPedagog: builder.query<AcademicSubject, LendaApiProps>(
      {
        query: args => ({
          url: getAcademicSubjectPerPedagogUrl(args),
        }),
        providesTags: ['allAcademicSubjects'],
      },
    ),
    getAcademicSubjectForStudent: builder.query<AcademicSubject, LendaApiProps>(
      {
        query: args => ({
          url: getAcademicSubjectPerPedagogUrl(args),
        }),
        providesTags: ['allAcademicSubjects'],
      },
    ),
    getDetyra: builder.query<Blob, IdEntity>({
      query: args => ({
        url: getDetyraUrl(args),
        responseHandler: handleDownloadResponse,
      }),
      providesTags: ['allAcademicSubjects'],
    }),

    updateTopicAssignment: builder.mutation<
      number,
      UpdateKlasaStudent<UpdateDetyraPedagog>
    >({
      query: ({ id, body }) => ({
        url: `/detyra/${id}`,
        method: 'PUT',
        body: body,
      }),
      invalidatesTags: ['allAcademicSubjects'],
    }),

    uploadOrUpdateDetyra: builder.mutation<number, UploadDetyraParams>({
      query: ({ body, role, detyraId, detyraStudentId, lendaTemaId }) => {
        if (role === pedagog) {
          if (detyraId) {
            return {
              url: `/detyra/${detyraId}/dokument`,
              method: 'PUT',
              body,
            };
          }
          if (lendaTemaId) {
            return {
              url: `/lenda-tema/${lendaTemaId}/detyra`,
              method: 'POST',
              body,
            };
          }
        }

        if (role === student) {
          if (detyraStudentId) {
            return {
              url: `/detyra-student/${detyraStudentId}/dokument`,
              method: 'PUT',
              body,
            };
          }
          if (detyraId) {
            return {
              url: `/detyra/${detyraId}/ngarko-zgjidhje`,
              method: 'POST',
              body,
            };
          }
        }

        throw new Error('Invalid parameters for uploadOrUpdateDetyra');
      },
      invalidatesTags: ['allAcademicSubjects'],
    }),
    getMaterialsPerTopic: builder.query<Materials, MaterialsProps>({
      query: ({ id, size, page }) => ({
        url: `/lenda-tema/${id}/materiale/per-pedagog?page=${page}&size=${size}`,
      }),
      providesTags: ['allAcademicSubjects'],
    }),
    uploadMaterialsPedagog: builder.mutation<number, FormDataType>({
      query: ({ id, body }) => ({
        url: `lenda-tema/${id}/materiale`,
        method: 'POST',
        body: body,
      }),
      invalidatesTags: ['allAcademicSubjects'],
    }),

    updateMaterialPerTopic: builder.mutation<
      number,
      UpdateKlasaStudent<UpdateMaterialPedagog>
    >({
      query: ({ id, body }) => ({
        url: `/materiale/${id}`,
        method: 'PUT',
        body: body,
      }),
      invalidatesTags: ['allAcademicSubjects'],
    }),
    updateMaterialDocumentPerTopic: builder.mutation<number, FormDataType>({
      query: ({ id, body }) => ({
        url: `/materiale/${id}/dokument`,
        method: 'PUT',
        body: body,
      }),
      invalidatesTags: ['allAcademicSubjects'],
    }),

    exportDocumentPerTopic: builder.query<Blob, IdEntity>({
      query: args => ({
        url: getUrl(args),
        responseHandler: handleDownloadResponse,
      }),
      providesTags: ['allAcademicSubjects'],
    }),
    deleteMaterialPerTopic: builder.mutation<string, number>({
      query: id => ({
        url: `/materiale/${id}`,
        method: 'DELETE',
        responseHandler: response => response.text(),
      }),
      invalidatesTags: ['allAcademicSubjects'],
    }),
    getMaterialsPerTopicAdmin: builder.query<Materials, MaterialsProps>({
      query: ({ id, size, page }) => ({
        url: `/lenda-tema/${id}/materiale?page=${page}&size=${size}`,
      }),
      providesTags: ['allAcademicSubjects'],
    }),
    getMaterialsPerTopicStudent: builder.query<Materials, MaterialsProps>({
      query: ({ id, size, page }) => ({
        url: `/lenda-tema/${id}/materiale/per-student?page=${page}&size=${size}`,
      }),
      providesTags: ['allAcademicSubjects'],
    }),
  }),
});

export const {
  useGetAllAcademicSubjectsQuery,
  useLazyGetAllAcademicSubjectsQuery,
  useGetAcademicSubjectByIdQuery,
  useLazyGetAcademicSubjectByIdQuery,
  useDeleteAcademicSubjectMutation,
  useUpdateAcademicSubjectMutation,
  useAddAcademicSubjectMutation,
  useGetAcademicSubjectForPedagogQuery,
  useLazyGetAcademicSubjectForPedagogQuery,
  useGetAcademicSubjectForStudentQuery,
  useGetKlasaLendaQuery,
  useGetKlasaLendaPerPedagogQuery,
  useGetTemaPerLendeQuery,
  useLazyGetTemaPerLendeQuery,
  useGetTemaPerLendeStudentQuery,
  useGetTemaPerLendeAdminQuery,
  useLazyGetDetyraQuery,
  useUpdateTopicsPerSubjectMutation,
  useUpdateSingleTopicMutation,
  useUpdateTeachersPerSuubjectMutation,
  useUpdateSingleTeacherMutation,
  useUpdateTopicAssignmentMutation,
  useUploadMaterialsPedagogMutation,
  useGetMaterialsPerTopicQuery,
  useDeleteMaterialPerTopicMutation,
  useUpdateMaterialDocumentPerTopicMutation,
  useUpdateMaterialPerTopicMutation,
  useGetMaterialsPerTopicAdminQuery,
  useGetMaterialsPerTopicStudentQuery,
  useLazyExportDocumentPerTopicQuery,
  useUploadOrUpdateDetyraMutation,
} = lendeVitiAkademikApi;
